import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { FixedSizeGrid as Grid } from 'react-window';
import axios from 'axios';
import { TextField, Typography, CircularProgress, Button, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import { FaList, FaThLarge } from 'react-icons/fa';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    backgroundColor: '#000000',
    color: '#ffffff',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '10px 16px 6px',
  },
  title: {
    fontSize: '2.2rem',
    fontWeight: 'bold',
    margin: 0,
    padding: 0,
    color: '#ffffff',
    textShadow: '0 0 15px rgba(77, 121, 255, 0.8)',
  },
  subtitle: {
    marginTop: '6px',
    color: '#cccccc',
  },
  searchRow: {
    padding: '10px 16px 12px',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  viewToggleIcon: {
    color: '#ffffff',
    border: '1px solid rgba(77,121,255,0.55)',
    borderRadius: 8,
    padding: 8,
  },
  viewToggleIconActive: {
    color: '#4d79ff',
    border: '1px solid rgba(77,121,255,0.95)',
    borderRadius: 8,
    padding: 8,
  },
  searchField: {
    flex: 1,
    '& .MuiInputBase-input': {
      color: '#ffffff',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#4d79ff',
      },
      '&:hover fieldset': {
        borderColor: '#6b8fff',
      },
    },
  },
  listWrap: {
    flex: 1,
    minHeight: 0,
    borderTop: '1px solid rgba(77,121,255,0.35)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
  },
  actionsRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginTop: 2,
  },
  actionButton: {
    borderColor: 'rgba(77,121,255,0.55)',
    color: '#ffffff',
    textTransform: 'none',
  },
  imageZoomWrap: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    cursor: 'zoom-in',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    transition: 'transform 0.1s ease-out',
  },
  thumb: {
    width: '160px',
    height: '90px',
    backgroundColor: '#000000',
    border: '1px solid rgba(77,121,255,0.45)',
    borderRadius: '10px',
    overflow: 'hidden',
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    position: 'relative',
  },
  thumbImageArea: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  gridRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    boxSizing: 'border-box',
  },
  gridCard: {
    flex: 1,
    minWidth: 0,
    border: '1px solid rgba(77,121,255,0.45)',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: 'rgba(0,0,0,0.65)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  gridImageWrap: {
    width: '100%',
    height: 260,
    backgroundColor: '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridOverlayBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '4px 8px',
    pointerEvents: 'auto',
  },
  gridOverlayTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#ffffff',
    textShadow: '0 0 10px rgba(0,0,0,0.9)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  gridOverlayActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  overlayButton: {
    borderColor: 'rgba(255,255,255,0.75)',
    color: '#ffffff',
    textTransform: 'none',
    padding: '2px 10px',
    minHeight: 28,
    backgroundColor: 'transparent',
  },
  gridImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  gridMeta: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minHeight: 74,
  },
  gridFilename: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#ffffff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  gridSubmeta: {
    color: '#cccccc',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowMeta: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px',
  },
  filename: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#ffffff',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  submeta: {
    color: '#cccccc',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  loadingRow: {
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#cccccc',
  },
}));

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export default function PcapGallery() {
  const classes = useStyles();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const missingKeysRef = useRef(new Set());
  const [imageScales, setImageScales] = useState({});

  const defaultScale = 1.5;

  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? window.localStorage.getItem('pcapGalleryViewMode') : '';
      return v === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  });

  const [viewportWidth, setViewportWidth] = useState(() => {
    try {
      return typeof window !== 'undefined' ? window.innerWidth : 1200;
    } catch (e) {
      return 1200;
    }
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const onResize = () => {
        try { setViewportWidth(window.innerWidth); } catch (e) {}
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('pcapGalleryViewMode', viewMode);
    } catch (e) {
    }
  }, [viewMode]);

  const limit = 80;
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const queryRef = useRef('');
  const imageCacheBustRef = useRef(String(Date.now()));

  const buildImageSrc = useCallback((row) => {
    const i = row && row.image_url ? String(row.image_url) : '';
    const t = row && row.thumbnail_url ? String(row.thumbnail_url) : '';
    const rel = i || t;
    if (!rel) return '';
    const cacheBust = imageCacheBustRef.current ? String(imageCacheBustRef.current) : '';
    const withBust = (s) => {
      if (!cacheBust) return s;
      if (String(s).includes('?')) return s;
      return `${s}?v=${encodeURIComponent(cacheBust)}`;
    };
    if (/^https?:\/\//i.test(rel)) return withBust(rel);
    if (rel.startsWith('/')) return withBust(rel);
    return withBust(`/${rel}`);
  }, []);

  const getRowKey = useCallback((row) => {
    try {
      const k = row && (row.graph_hash || row.graphHash || row.url || row.filename);
      return k ? String(k) : '';
    } catch (e) {
      return '';
    }
  }, []);

  const handleMissingImage = useCallback((row) => {
    const k = getRowKey(row);
    if (!k) return;
    if (missingKeysRef.current.has(k)) return;
    missingKeysRef.current.add(k);
    setItems((prev) => (Array.isArray(prev) ? prev.filter((it) => getRowKey(it) !== k) : prev));
  }, [getRowKey]);

  const getRowUrl = useCallback((row) => {
    try {
      const v = row && (row.url || row.pcap_url || row.pcapUrl || row.source_url || row.sourceUrl);
      return v ? String(v) : '';
    } catch (e) {
      return '';
    }
  }, []);

  const handleOpen = useCallback((url, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!url) return;

    // Set loading state in window BEFORE navigation so the new component knows to show progress
    try {
      if (typeof window !== 'undefined') {
        window.__E3D_PCAP_IS_LOADING_FROM_GALLERY__ = true;
        window.__E3D_PCAP_PROGRESS_VISIBLE__ = true;
        window.__E3D_PCAP_PROGRESS__ = 0;
        window.__E3D_PCAP_SHOW_OPEN_BUTTON__ = false;
      }
    } catch (e2) {}

    // Use React Router navigation instead of full page reload
    try {
      navigate(`/pcap?openUrl=${encodeURIComponent(url)}&_t=${Date.now()}`);
    } catch (e2) {
      console.error('Navigation error:', e2);
    }
  }, [navigate]);

  const handleDownload = useCallback((img, filename, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!img) return;
    try {
      const a = document.createElement('a');
      a.href = img;
      a.download = filename ? String(filename) : 'pcap_graph.png';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e2) {
      try {
        window.open(img, '_blank', 'noopener,noreferrer');
      } catch (e3) {
      }
    }
  }, []);

  const resetAndFetch = useCallback(async (q) => {
    const qq = String(q || '');
    queryRef.current = qq;
    offsetRef.current = 0;
    hasMoreRef.current = true;
    setItems([]);
    setTotal(0);
    setError('');

    setLoading(true);
    try {
      const resp = await axios.get(`${SERVER_NAME}/pcapGallery`, {
        params: {
          q: qq,
          limit,
          offset: 0,
        },
        withCredentials: false,
      });
      const data = resp && resp.data ? resp.data : {};
      const nextItems = Array.isArray(data.items) ? data.items : [];
      const nextTotal = data.total != null ? Number(data.total) : 0;
      setItems(nextItems);
      setTotal(Number.isFinite(nextTotal) ? nextTotal : 0);
      offsetRef.current = nextItems.length;
      hasMoreRef.current = nextItems.length < (Number.isFinite(nextTotal) ? nextTotal : nextItems.length);
    } catch (e) {
      setError('Failed to load gallery');
      setItems([]);
      setTotal(0);
      offsetRef.current = 0;
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loading) return;
    if (!hasMoreRef.current) return;

    const q = queryRef.current;
    const offset = offsetRef.current;

    setLoading(true);
    try {
      const resp = await axios.get(`${SERVER_NAME}/pcapGallery`, {
        params: {
          q,
          limit,
          offset,
        },
        withCredentials: false,
      });
      const data = resp && resp.data ? resp.data : {};
      const next = Array.isArray(data.items) ? data.items : [];
      const nextTotal = data.total != null ? Number(data.total) : total;

      setItems((prev) => {
        const merged = prev.concat(next);
        offsetRef.current = merged.length;
        hasMoreRef.current = merged.length < (Number.isFinite(nextTotal) ? nextTotal : merged.length);
        return merged;
      });

      if (Number.isFinite(nextTotal)) setTotal(nextTotal);
    } catch (e) {
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [loading, total]);

  const debouncedSearch = useMemo(() => debounce((q) => {
    resetAndFetch(q);
  }, 350), [resetAndFetch]);

  useEffect(() => {
    resetAndFetch('');
  }, [resetAndFetch]);

  const onItemsRendered = useCallback((info) => {
    try {
      const stop = info && info.visibleStopIndex != null ? Number(info.visibleStopIndex) : 0;
      if (!Number.isFinite(stop)) return;
      if (stop >= Math.max(0, items.length - 12)) {
        loadMore();
      }
    } catch (e) {
    }
  }, [items.length, loadMore]);

  const gridCols = useMemo(() => {
    const w = Number(viewportWidth) || 0;
    if (w <= 0) return 2;
    if (w < 820) return 1;
    if (w < 1320) return 2;
    return 3;
  }, [viewportWidth]);

  const gridRowCount = useMemo(() => {
    const cols = Math.max(1, Number(gridCols) || 1);
    const base = Math.ceil(items.length / cols);
    // No artificial floor of 1 here: with zero items and nothing left to
    // fetch, that used to force one permanent placeholder row whose "row"
    // lookup would always be undefined, rendering a "Loading..." spinner
    // forever even though the gallery is genuinely empty.
    return base + (hasMoreRef.current ? 1 : 0);
  }, [items.length, gridCols]);

  const onItemsRenderedGrid = useCallback((info) => {
    try {
      const stop = info && info.visibleStopIndex != null ? Number(info.visibleStopIndex) : 0;
      if (!Number.isFinite(stop)) return;
      const cols = Math.max(1, Number(gridCols) || 1);
      const thresholdRows = Math.ceil(12 / cols);
      const lastDataRow = Math.ceil(items.length / cols) - 1;
      if (stop >= Math.max(0, lastDataRow - thresholdRows)) {
        loadMore();
      }
    } catch (e) {
    }
  }, [gridCols, items.length, loadMore]);

  const Row = useCallback(({ index, style }) => {
    const row = items[index];
    if (!row) {
      return (
        <div style={style} className={classes.loadingRow}>
          <CircularProgress size={16} style={{ color: '#4d79ff' }} />
          Loading...
        </div>
      );
    }

    const filename = row && row.filename ? String(row.filename) : '';
    const url = getRowUrl(row);
    const thumb = buildImageSrc(row);
    const key = getRowKey(row);
    const scale = imageScales && key && typeof imageScales[key] !== 'undefined'
      ? (Number(imageScales[key]) || defaultScale)
      : defaultScale;

    return (
      <div
        style={style}
        className={classes.row}
        onClick={() => {
          if (url) handleOpen(url);
        }}
      >
        <div className={classes.thumb}>
          {thumb ? (
            <div className={classes.thumbImageArea}>
              <div
                className={classes.imageZoomWrap}
                onWheel={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!key) return;
                  const cur = (imageScales && typeof imageScales[key] !== 'undefined')
                    ? (Number(imageScales[key]) || defaultScale)
                    : defaultScale;
                  const next = cur + (e.deltaY > 0 ? -0.1 : 0.1);
                  const clamped = Math.min(Math.max(next, 0.5), 3);
                  setImageScales((prev) => ({ ...prev, [key]: clamped }));
                }}
                onClick={(e) => {
                  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                }}
              >
                <img
                  className={classes.zoomImg}
                  src={thumb}
                  alt={filename}
                  loading="lazy"
                  onError={() => handleMissingImage(row)}
                  style={{ transform: `scale(${scale})` }}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className={classes.rowMeta}>
          <div className={classes.filename} title={filename}>{filename || url}</div>
          <div className={classes.actionsRow} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outlined"
              size="small"
              className={classes.actionButton}
              onClick={(e) => handleOpen(url, e)}
            >
              Open
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.actionButton}
              onClick={(e) => handleDownload(url, filename || 'download.pcap', e)}
            >
              Download
            </Button>
          </div>
        </div>
      </div>
    );
  }, [items, classes, buildImageSrc, getRowKey, imageScales, handleOpen, handleDownload, defaultScale, handleMissingImage]);

  const GridRow = useCallback(({ index, style }) => {
    const cols = Math.max(1, Number(gridCols) || 1);
    const start = index * cols;
    const cells = [];
    for (let c = 0; c < cols; c += 1) {
      const row = items[start + c];
      if (!row) {
        if (hasMoreRef.current && start + c >= items.length) {
          cells.push(
            <div key={`loading-${c}`} className={classes.gridCard} style={{ visibility: 'hidden' }} />
          );
        }
        continue;
      }
      const filename = row && row.filename ? String(row.filename) : '';
      const url = getRowUrl(row);
      const img = buildImageSrc(row);
      const key = getRowKey(row);
      const scale = imageScales && key && typeof imageScales[key] !== 'undefined'
        ? (Number(imageScales[key]) || defaultScale)
        : defaultScale;
      cells.push(
        <div
          key={row.graph_hash || url || `${start}-${c}`}
          className={classes.gridCard}
          onClick={() => {
            if (url) handleOpen(url);
          }}
        >
          <div className={classes.gridOverlayBar} onClick={(e) => e.stopPropagation()}>
            <div className={classes.gridOverlayTitle} title={filename}>{filename || url}</div>
            <div className={classes.gridOverlayActions}>
              <Button
                variant="outlined"
                size="small"
                className={classes.overlayButton}
                onClick={(e) => handleOpen(url, e)}
              >
                Open
              </Button>
              <Button
                variant="outlined"
                size="small"
                className={classes.overlayButton}
                onClick={(e) => handleDownload(url, filename || 'download.pcap', e)}
              >
                Download
              </Button>
            </div>
          </div>
          <div className={classes.gridImageWrap}>
            {img ? (
              <div
                className={classes.imageZoomWrap}
                onWheel={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!key) return;
                  const cur = (imageScales && typeof imageScales[key] !== 'undefined')
                    ? (Number(imageScales[key]) || defaultScale)
                    : defaultScale;
                  const next = cur + (e.deltaY > 0 ? -0.1 : 0.1);
                  const clamped = Math.min(Math.max(next, 0.5), 3);
                  setImageScales((prev) => ({ ...prev, [key]: clamped }));
                }}
                onClick={(e) => {
                  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                }}
              >
                <img
                  className={classes.zoomImg}
                  src={img}
                  alt={filename}
                  loading="lazy"
                  onError={() => handleMissingImage(row)}
                  style={{ transform: `scale(${scale})` }}
                />
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    if (!cells.length) {
      return (
        <div style={style} className={classes.loadingRow}>
          <CircularProgress size={16} style={{ color: '#4d79ff' }} />
          Loading...
        </div>
      );
    }

    while (cells.length < cols) {
      cells.push(<div key={`pad-${cells.length}`} className={classes.gridCard} style={{ visibility: 'hidden' }} />);
    }

    return (
      <div style={style} className={classes.gridRow}>
        {cells}
      </div>
    );
  }, [items, classes, buildImageSrc, gridCols, getRowKey, imageScales, handleOpen, handleDownload, defaultScale, handleMissingImage]);

  const listHeight = useMemo(() => {
    try {
      const h = typeof window !== 'undefined' ? window.innerHeight : 800;
      return Math.max(240, h - 260);
    } catch (e) {
      return 600;
    }
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.title}>PCAP Gallery</Typography>
      </div>

      <div className={classes.searchRow}>
        <TextField
          variant="outlined"
          placeholder="Search filename (use * wildcards)"
          className={classes.searchField}
          value={query}
          onChange={(e) => {
            const v = e && e.target ? e.target.value : '';
            setQuery(v);
            debouncedSearch(v);
          }}
        />
        <Tooltip title="List view">
          <IconButton
            className={viewMode === 'list' ? classes.viewToggleIconActive : classes.viewToggleIcon}
            onClick={() => setViewMode('list')}
          >
            <FaList />
          </IconButton>
        </Tooltip>
        <Tooltip title="Grid view">
          <IconButton
            className={viewMode === 'grid' ? classes.viewToggleIconActive : classes.viewToggleIcon}
            onClick={() => setViewMode('grid')}
          >
            <FaThLarge />
          </IconButton>
        </Tooltip>
        <Typography style={{ color: '#cccccc', minWidth: 90, textAlign: 'right' }}>
          {total ? `${items.length}/${total}` : (items.length ? `${items.length}` : '')}
        </Typography>
      </div>

      {error ? (
        <div className={classes.loadingRow}>{error}</div>
      ) : null}

      {(!loading && !error && items.length === 0 && !hasMoreRef.current) ? (
        <div className={classes.loadingRow}>
          {query ? 'No pcap samples match your search.' : 'No pcap samples in the gallery yet.'}
        </div>
      ) : (
        <div className={classes.listWrap}>
          <List
            height={listHeight}
            itemCount={viewMode === 'grid' ? gridRowCount : (items.length + (hasMoreRef.current ? 1 : 0))}
            itemSize={viewMode === 'grid' ? 320 : 110}
            width={'100%'}
            onItemsRendered={viewMode === 'grid' ? onItemsRenderedGrid : onItemsRendered}
          >
            {viewMode === 'grid' ? GridRow : Row}
          </List>
        </div>
      )}

      {loading ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={16} style={{ color: '#4d79ff' }} />
          Loading...
        </div>
      ) : null}
    </div>
  );
}
